import dayjs from 'dayjs';

import { PickerItemKind, PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';

import {
  CreateAcknowledgementRequestBody,
  PreviewRecipientsBody,
  SkippedRecipient,
} from 'utils/api/modules/entities/entities.types';

import { AcknowledgementRequestFormValues } from './schema';

import { FieldErrorMessages, FormField, FormikFieldErrors } from './types';

const FIELD_MAP: Record<string, FormField> = {
  name: 'name',
  document: 'document',
  recipients: 'recipients',
  recipientGroups: 'recipients',
  dueDate: 'dueDate',
};

// Keeps only the keys the form knows about, so an unrecognised backend key can't land in Formik state.
export const mapFieldErrors = (errors: Record<string, string[]> | undefined): FieldErrorMessages => {
  const mapped: FieldErrorMessages = {};
  Object.entries(errors ?? {}).forEach(([key, value]) => {
    const formKey = FIELD_MAP[key];
    if (!formKey) return;
    const messages = Array.isArray(value) ? value.map(String) : [String(value)];
    // `recipients` and `recipientGroups` share one Formik field — collect both so neither is lost.
    mapped[formKey] = [...(mapped[formKey] ?? []), ...messages];
  });
  return mapped;
};

export const toFormikErrors = (errors: FieldErrorMessages): FormikFieldErrors => {
  const formikErrors: FormikFieldErrors = {};
  (Object.keys(errors) as FormField[]).forEach((key) => {
    formikErrors[key] = (errors[key] ?? []).join('\n');
  });
  return formikErrors;
};

export const skippedFrom = (data: unknown): SkippedRecipient[] => {
  if (!data || typeof data !== 'object' || !('skipped' in data)) return [];
  const { skipped } = data as { skipped: unknown };
  return Array.isArray(skipped) ? (skipped as SkippedRecipient[]) : [];
};

export const groupsIn = (recipients: PickerValue[]): PickerValue[] =>
  recipients.filter((item) => item.kind === PickerItemKind.Group);

// Upper bound only — sums group member counts without deduping overlaps (the client has counts,
// not member identities). Used as a fallback when the exact server-side preview is unavailable.
export const estimatedRecipientCount = (recipients: PickerValue[]): number =>
  recipients.reduce((total, item) => total + (item.kind === PickerItemKind.Group ? item.memberCount : 1), 0);

export const buildPreviewBody = (recipients: PickerValue[]): PreviewRecipientsBody => {
  const users = recipients.filter((item) => item.kind === PickerItemKind.User).map((item) => item.uuid);
  const groups = recipients.filter((item) => item.kind === PickerItemKind.Group).map((item) => item.uuid);
  return { recipients: users, ...(groups.length > 0 && { recipientGroups: groups }) };
};

// `recipientGroups` is omitted rather than sent empty, so an individual-only send stays
// byte-identical to the pre-groups request and can't trip the flag-off rejection.
export const buildCreateBody = (values: {
  name: string;
  document: NonNullable<AcknowledgementRequestFormValues['document']>;
  dueDate: string;
  recipients: PickerValue[];
}): CreateAcknowledgementRequestBody => {
  const recipients = values.recipients.filter((item) => item.kind === PickerItemKind.User).map((item) => item.uuid);
  const recipientGroups = values.recipients
    .filter((item) => item.kind === PickerItemKind.Group)
    .map((item) => item.uuid);

  return {
    name: values.name.trim(),
    document: values.document.uuid,
    dueDate: dayjs(values.dueDate).format('YYYY-MM-DD'),
    recipients,
    ...(recipientGroups.length > 0 && { recipientGroups }),
  };
};
